/**
 * Personalization toggle for sak.sak
 *
 * Drives the Custom text Yes/No UI and wires it to the
 * underlying Style variant option (Standard / Personalized).
 *
 * Survives section re-renders triggered by variant changes by
 * storing the typed text in sessionStorage and re-applying it.
 */
(function () {
  'use strict';

  function initAllToggles() {
    document.querySelectorAll('.personalization-toggle').forEach(initToggle);
    document.querySelectorAll('.personalization-required').forEach(initRequiredInput);
  }

  // Init on DOMContentLoaded and also on demand
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllToggles);
  } else {
    initAllToggles();
  }

  function storageKey(sectionId, productId) {
    return 'saksak_personalization_' + productId + '_' + sectionId;
  }

  function saveState(sectionId, productId, value, text) {
    try {
      sessionStorage.setItem(storageKey(sectionId, productId), JSON.stringify({ value: value, text: text }));
    } catch (_) { /* silent */ }
  }

  function loadState(sectionId, productId) {
    try {
      var raw = sessionStorage.getItem(storageKey(sectionId, productId));
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function nameOnly(value) {
    var text = (value || '').trim();
    return text.toLowerCase().endsWith('.sak') ? text.slice(0, -4) : text;
  }

  function fullCustomText(value) {
    var text = nameOnly(value);
    return text ? text + '.sak' : '';
  }

  function syncFullText(textInput, fullTextInput, shouldSubmit) {
    if (!fullTextInput) return;
    fullTextInput.value = fullCustomText(textInput.value);
    if (shouldSubmit && fullTextInput.value) {
      fullTextInput.setAttribute('name', 'properties[Custom text]');
    } else {
      fullTextInput.removeAttribute('name');
    }
  }

  function getQuantityUI(sectionId) {
    var block = document.querySelector('#Quantity-Form-' + sectionId);
    if (!block) return null;

    return {
      block: block,
      input: block.querySelector('.quantity__input'),
      buttons: block.querySelectorAll('.quantity__button')
    };
  }

  function lockQuantity(sectionId, noteEl) {
    var ui = getQuantityUI(sectionId);
    if (!ui || !ui.input) return;

    if (!ui.input.dataset.personalizationOriginalValue) {
      ui.input.dataset.personalizationOriginalValue = ui.input.value || '1';
    }

    ui.input.value = '1';
    ui.input.setAttribute('value', '1');
    ui.input.readOnly = true;
    ui.input.setAttribute('aria-readonly', 'true');
    ui.block.setAttribute('hidden', '');

    ui.buttons.forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.tabIndex = -1;
    });

    if (noteEl) noteEl.hidden = false;
  }

  function unlockQuantity(sectionId, noteEl) {
    var ui = getQuantityUI(sectionId);
    if (!ui || !ui.input) return;

    ui.block.removeAttribute('hidden');
    ui.input.readOnly = false;
    ui.input.removeAttribute('aria-readonly');

    if (ui.input.dataset.personalizationOriginalValue) {
      ui.input.value = ui.input.dataset.personalizationOriginalValue;
      ui.input.setAttribute('value', ui.input.dataset.personalizationOriginalValue);
    }

    ui.buttons.forEach(function (button) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.tabIndex = 0;
    });

    if (noteEl) noteEl.hidden = true;
  }

  function initToggle(container) {
    // Prevent double-init
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    var sectionId = container.dataset.sectionId;
    var productId = container.dataset.productId;
    var optionName = container.dataset.optionName;
    var productFormId = container.dataset.productFormId;
    var submitting = false;
    var unsubscribeVariantChange;
    var unsubscribeCartUpdate;
    var unsubscribeCartError;

    var buttons = container.querySelectorAll('.personalization-toggle__btn');
    var inputWrap = container.querySelector('.personalization-toggle__input-wrap');
    var textInput = container.querySelector('.personalization-toggle__text-input');
    var fullTextInput = container.querySelector('.personalization-toggle__full-text-input');
    var quantityNote = container.querySelector('.personalization-toggle__quantity-note');

    if (!inputWrap || !textInput || !fullTextInput || buttons.length === 0) return;

    // Restore state from session (survives re-renders)
    var saved = loadState(sectionId, productId);
    if (saved) {
      applyState(saved.value, saved.text, false);
    }

    function teardown() {
      if (typeof unsubscribeVariantChange === 'function') unsubscribeVariantChange();
      if (typeof unsubscribeCartUpdate === 'function') unsubscribeCartUpdate();
      if (typeof unsubscribeCartError === 'function') unsubscribeCartError();
      unsubscribeVariantChange = null;
      unsubscribeCartUpdate = null;
      unsubscribeCartError = null;
    }

    function isDetached() {
      return !document.body.contains(container);
    }

    // Find the native Style dropdown inside variant-selects
    function getStyleSelect() {
      var vs = document.querySelector('#variant-selects-' + sectionId);
      if (!vs) return null;
      return vs.querySelector('select[name="options[' + optionName + ']"]');
    }

    // Hide the native Style dropdown
    function hideStyleDropdown() {
      var sel = getStyleSelect();
      if (!sel) return;
      var wrapper = sel.closest('.product-form__input--dropdown');
      if (wrapper) {
        wrapper.style.display = 'none';
      }
    }
    hideStyleDropdown();

    // Button click handler
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.value;
        applyState(value, value === 'yes' ? textInput.value : '', true);
      });
    });

    // Save text as user types
    textInput.addEventListener('input', function () {
      var cleanText = nameOnly(textInput.value);
      if (cleanText !== textInput.value.trim()) {
        textInput.value = cleanText;
      }
      var activeBtn = container.querySelector('.personalization-toggle__btn--active');
      var currentValue = activeBtn ? activeBtn.dataset.value : 'no';
      syncFullText(textInput, fullTextInput, currentValue === 'yes');
      saveState(sectionId, productId, currentValue, textInput.value);
    });

    // Find the product form to intercept submission
    var productForm = document.getElementById(productFormId);
    if (productForm) {
      productForm.addEventListener('submit', onFormSubmit, true);
    }

    function applyState(value, text, triggerVariant) {
      var isYes = value === 'yes';

      // Update button visual states
      buttons.forEach(function (btn) {
        var active = btn.dataset.value === value;
        btn.classList.toggle('personalization-toggle__btn--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // Show/hide text input
      if (isYes) {
        inputWrap.removeAttribute('hidden');
        textInput.setAttribute('required', '');
        if (text) textInput.value = nameOnly(text);
        syncFullText(textInput, fullTextInput, true);
        lockQuantity(sectionId, quantityNote);
      } else {
        inputWrap.setAttribute('hidden', '');
        textInput.removeAttribute('required');
        textInput.value = '';
        fullTextInput.value = '';
        fullTextInput.removeAttribute('name');
        unlockQuantity(sectionId, quantityNote);
      }

      // Persist
      saveState(sectionId, productId, value, isYes ? textInput.value : '');

      // Switch the native Style dropdown
      if (triggerVariant) {
        var styleSelect = getStyleSelect();
        if (!styleSelect) return;
        var matchingOption;
        if (isYes) {
          matchingOption = Array.from(styleSelect.options).find(function (opt) {
            var v = opt.value.toLowerCase();
            return v === 'personalised' || v === 'personalized';
          });
        } else {
          matchingOption = Array.from(styleSelect.options).find(function (opt) {
            return opt.value.toLowerCase() === 'standard';
          });
        }
        if (matchingOption) {
          styleSelect.value = matchingOption.value;
          styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    function onFormSubmit(e) {
      // Only validate when personalized is active
      if (textInput.hasAttribute('required') && !textInput.value.trim()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitting = false;
        textInput.focus();
        textInput.setCustomValidity('Please enter your custom text');
        textInput.reportValidity();
        textInput.addEventListener('input', function clearValidity() {
          textInput.setCustomValidity('');
          textInput.removeEventListener('input', clearValidity);
        });
        return false;
      }
      // If not personalized, strip the property so it doesn't appear on the line item
      if (!textInput.hasAttribute('required') || !textInput.value.trim()) {
        fullTextInput.removeAttribute('name');
        fullTextInput.value = '';
      } else {
        textInput.value = nameOnly(textInput.value);
        syncFullText(textInput, fullTextInput, true);
      }
      textInput.removeAttribute('name');
      submitting = true;
    }

    // Re-init after variant-driven re-render
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (isDetached()) {
          teardown();
          return;
        }
        if (event.data.sectionId !== sectionId) return;
        requestAnimationFrame(function () {
          // Re-hide the style dropdown (fresh HTML after re-render)
          hideStyleDropdown();
          // If our container was swapped, re-init the fresh one
          var fresh = document.querySelector('#PersonalizationToggle-' + sectionId);
          if (fresh && fresh.dataset.initialized !== 'true') {
            initToggle(fresh);
          }
        });
      });

      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (isDetached()) {
          teardown();
          return;
        }
        if (!submitting || event.source !== 'product-form') return;
        try {
          sessionStorage.removeItem(storageKey(sectionId, productId));
        } catch (_) { /* silent */ }
        submitting = false;
      });

      unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, function () {
        if (isDetached()) {
          teardown();
          return;
        }
        submitting = false;
      });
    }
  }

  function initRequiredInput(container) {
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    var sectionId = container.dataset.sectionId;
    var productId = container.dataset.productId;
    var productFormId = container.dataset.productFormId;
    var submitting = false;
    var unsubscribeVariantChange;
    var unsubscribeCartUpdate;
    var unsubscribeCartError;

    var textInput = container.querySelector('.personalization-required__text-input');
    var fullTextInput = container.querySelector('.personalization-required__full-text-input');
    var quantityNote = container.querySelector('.personalization-required__quantity-note');
    if (!textInput || !fullTextInput) return;

    var saved = loadState(sectionId, productId);
    if (saved && saved.text) {
      textInput.value = nameOnly(saved.text);
    }
    syncFullText(textInput, fullTextInput, true);

    lockQuantity(sectionId, quantityNote);

    function teardown() {
      if (typeof unsubscribeVariantChange === 'function') unsubscribeVariantChange();
      if (typeof unsubscribeCartUpdate === 'function') unsubscribeCartUpdate();
      if (typeof unsubscribeCartError === 'function') unsubscribeCartError();
      unsubscribeVariantChange = null;
      unsubscribeCartUpdate = null;
      unsubscribeCartError = null;
    }

    function isDetached() {
      return !document.body.contains(container);
    }

    textInput.addEventListener('input', function () {
      var cleanText = nameOnly(textInput.value);
      if (cleanText !== textInput.value.trim()) {
        textInput.value = cleanText;
      }
      syncFullText(textInput, fullTextInput, true);
      saveState(sectionId, productId, 'required', textInput.value);
    });

    var productForm = document.getElementById(productFormId);
    if (productForm) {
      productForm.addEventListener('submit', onFormSubmit, true);
    }

    function onFormSubmit(e) {
      if (!textInput.value.trim()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitting = false;
        textInput.focus();
        textInput.setCustomValidity('Please enter your custom text');
        textInput.reportValidity();
        textInput.addEventListener('input', function clearValidity() {
          textInput.setCustomValidity('');
          textInput.removeEventListener('input', clearValidity);
        });
        return false;
      }

      textInput.value = nameOnly(textInput.value);
      syncFullText(textInput, fullTextInput, true);
      textInput.removeAttribute('name');
      submitting = true;
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (isDetached()) {
          teardown();
          return;
        }
        if (event.data.sectionId !== sectionId) return;
        requestAnimationFrame(function () {
          var fresh = document.querySelector('#PersonalizationRequired-' + sectionId);
          if (fresh && fresh.dataset.initialized !== 'true') {
            initRequiredInput(fresh);
          }
        });
      });

      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (isDetached()) {
          teardown();
          return;
        }
        if (!submitting || event.source !== 'product-form') return;
        try {
          sessionStorage.removeItem(storageKey(sectionId, productId));
        } catch (_) { /* silent */ }
        submitting = false;
      });

      unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, function () {
        if (isDetached()) {
          teardown();
          return;
        }
        submitting = false;
      });
    }
  }
})();
